/*
  The sale strip's behaviour: reveal, dismissal, scroll arrows, and the peek panel.

  Plain DOM work with no build step and no dependency on this theme's section registry, so it
  keeps working in the theme editor — where a section is re-rendered on every settings change —
  without needing to be registered anywhere. Everything is delegated from the section element,
  and the whole thing is idempotent: running twice on the same element does nothing the second
  time.

  Product data is fetched from /products/{handle}.js when a panel opens rather than rendered
  into the page for every tile. Sixteen products' worth of variant JSON at the top of every
  page would be a real weight to carry for a panel most shoppers never open.
*/
(function () {
  'use strict';

  /*
    Shopify's money_format is allowed to contain MARKUP, and several of these shops' currencies
    use it: the AUD format is `<span class=money>${{amount_no_decimals}} AUD</span>`. The tile
    is rendered by Liquid's `money` filter, which emits that as HTML and shows "$120 AUD". The
    panel writes prices with textContent — correctly, since building them with innerHTML would
    put a merchant-editable string into the DOM as markup — so the same format would print the
    tag characters literally, about 60px below a tile showing the price properly. The Add
    button was the worst of it: "ADD TO BAG — <SPAN CLASS=MONEY>$120 AUD</SPAN>", uppercased by
    its own text-transform.

    Stripped once, here, rather than escaping at each use.
  */
  var MONEY_FORMAT = ((window.theme && window.theme.moneyFormat) || '${{amount}}')
    .replace(/<[^>]*>/g, '');

  /*
    Shopify's money filter lives on the server, so a price rendered by JavaScript has to be
    formatted here. This covers the four formats Shopify actually emits. Anything unrecognised
    falls back to the raw amount rather than guessing, because a wrong currency figure beside
    an Add to bag button is worse than a plain one.
  */
  function formatMoney(cents, format) {
    var fmt = format || MONEY_FORMAT;
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var frac = parts[1];

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return whole + '.' + frac;
        case 'amount_no_decimals': return whole;
        case 'amount_with_comma_separator': return whole.replace(/,/g, '.') + ',' + frac;
        case 'amount_no_decimals_with_comma_separator': return whole.replace(/,/g, '.');
        default: return whole + '.' + frac;
      }
    });
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function init(section) {
    if (section.dataset.sbReady === '1') return;
    section.dataset.sbReady = '1';

    var rail = section.querySelector('[data-rail]');
    var prev = section.querySelector('.SaleBanner__Arrow--prev');
    var next = section.querySelector('.SaleBanner__Arrow--next');
    var dismiss = section.querySelector('[data-dismiss]');
    var storageKey = section.dataset.storageKey;
    var dismissible = section.dataset.dismissible === 'true';

    /* ── a different selection on every load ──────────────────────────────────

       Liquid renders a pool larger than the strip shows; the draw happens here.

       It has to happen here rather than on the server, because Shopify serves rendered pages
       from an edge cache: a shuffle done in Liquid picks once and then every visitor receives
       that same pick until the entry expires, which is the bug this fixes wearing a different
       hat. Drawing in the browser is per-visit whatever sits in front of it.

       Before the reveal below, deliberately. Trimming afterwards would show the entire pool
       for a frame and then snatch two thirds of it off the screen.

       Not in the theme editor. The editor re-renders the section on every settings change, so
       shuffling there makes the tiles jump on each keystroke, and under Hand-picked it would
       scramble the order the merchant is in the middle of dragging into place. They see the
       first few in their own order; the live page is where rotation is worth looking at. */
    function paintImages(tiles) {
      tiles.forEach(function (tile) {
        var img = tile.querySelector('img[data-src]');
        if (!img) return;
        img.src = img.getAttribute('data-src');
        img.removeAttribute('data-src');
      });
    }

    /*
      BEST STOCKED FIRST.

      The strip leads with the pieces a shopper is most likely to find in her size.
      `data-sizes` counts variants holding real units and `data-units` sums them, both
      worked out in Liquid on the tile — see sale-banner-item.liquid for why availability
      is the wrong thing to count on this shop.

      This runs AFTER the shuffle below, and that division of labour is the point: the
      shuffle decides WHICH pieces are drawn from the pool, so the strip still varies
      between visits, and this decides the order they are shown in. Sorting before the
      shuffle would be pointless, and shuffling after this would undo it.

      Array.prototype.sort is stable, so pieces holding equal stock keep the shuffled
      order and the strip does not freeze into one sequence among its best-stocked few.
    */
    function stockedFirst(a, b) {
      var aSizes = parseInt(a.getAttribute('data-stock-sizes'), 10) || 0;
      var bSizes = parseInt(b.getAttribute('data-stock-sizes'), 10) || 0;
      if (bSizes !== aSizes) return bSizes - aSizes;
      var aUnits = parseInt(a.getAttribute('data-stock-units'), 10) || 0;
      var bUnits = parseInt(b.getAttribute('data-stock-units'), 10) || 0;
      return bUnits - aUnits;
    }

    // Re-seats tiles ahead of the See all link, which is a child of the rail too and
    // would otherwise end up in the middle of the row.
    function seat(tiles) {
      var seeAll = rail.querySelector('.SaleBanner__All');
      tiles.forEach(function (tile) { rail.insertBefore(tile, seeAll || null); });
    }

    /*
      Pieces with nothing actually in stock are dropped before anything else happens.

      Liquid admits a piece on `available`, which stays true for a variant whose inventory
      policy is `continue` — it keeps selling at zero. Such a piece reaches the strip, and
      opening it shows every size struck through with the button stuck on "Choose an
      option": a tile that cannot be bought, in a strip whose whole job is selling.

      Only ever when something is left standing. If a sale really is down to its last
      oversold pieces, showing them beats showing an empty bar.
    */
    function pruneUnbuyable() {
      if (!rail) return;
      var all = Array.prototype.slice.call(rail.querySelectorAll('.SaleBanner__Item'));
      var buyable = all.filter(function (t) { return (parseInt(t.getAttribute('data-stock-sizes'), 10) || 0) > 0; });
      if (!buyable.length || buyable.length === all.length) return;
      all.forEach(function (t) {
        if (buyable.indexOf(t) === -1 && t.parentNode) t.parentNode.removeChild(t);
      });
    }
    pruneUnbuyable();

    if (rail && rail.dataset.rotate === 'true') {
      var show = parseInt(rail.dataset.show, 10) || 0;
      var tiles = Array.prototype.slice.call(rail.querySelectorAll('.SaleBanner__Item'));

      if (!(window.Shopify && window.Shopify.designMode)) {
        // Fisher-Yates. Shuffled even when the pool is no larger than the strip, so a small
        // sale still varies in order instead of looking frozen.
        for (var i = tiles.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var swap = tiles[i]; tiles[i] = tiles[j]; tiles[j] = swap;
        }
      }

      if (show > 0 && tiles.length > show) {
        tiles.slice(show).forEach(function (tile) { tile.parentNode.removeChild(tile); });
        tiles = tiles.slice(0, show);
      }

      tiles.sort(stockedFirst);
      seat(tiles);
      paintImages(tiles);
    } else if (rail) {
      // Rotation off: nothing was drawn, but the strip is still ordered best stocked
      // first, so the setting changes how much varies and never whether the ordering
      // holds.
      var fixed = Array.prototype.slice.call(rail.querySelectorAll('.SaleBanner__Item'));
      fixed.sort(stockedFirst);
      seat(fixed);
      // Rotation off renders `src` directly, so this finds nothing. It is here for the moment
      // the setting is switched in the editor and one render is briefly out of step.
      paintImages(fixed);
    }

    /*
      Hidden in the markup and revealed here, so a shopper who dismissed it never sees it
      flash on the next page load. The cost is that it does not appear at all without
      JavaScript — acceptable for a promotional strip, and the alternative (render visible,
      hide on load) is a visible flicker on every single page.

      In the theme editor it always shows: a merchant who dismissed it once would otherwise
      think they had broken the section.
    */
    /*
      A dismissal lapses. It used to be permanent.

      The old value was the string '1' against a key that never changed, so one press of the ✕
      hid the strip on that device for ever — through new sales, new collections, and the
      merchant switching the section off and on again to try to bring it back. "I disabled it
      and enabled it and now it will not show" is exactly what that bug looks like from the
      other side, and nothing in the theme editor could reveal it.

      Fourteen days: long enough that dismissing it means something for the visit and the week
      after, short enough that a shopper is asked again about a sale that is genuinely new.
      Anything unparseable — including the old '1' — is treated as expired, so this upgrade
      un-hides the strip for everyone it had silently hidden.
    */
    var DISMISS_DAYS = 14;
    var dismissed = false;
    var inEditor = !!(window.Shopify && window.Shopify.designMode);
    if (dismissible && !inEditor) {
      try {
        var raw = window.localStorage.getItem(storageKey);
        var at = raw ? parseInt(raw, 10) : 0;
        dismissed = !!at && (Date.now() - at) < DISMISS_DAYS * 86400000;
      } catch (e) { /* private mode */ }
    }
    if (!dismissed) section.hidden = false;

    if (dismiss) {
      dismiss.addEventListener('click', function () {
        section.hidden = true;
        // Stop the timer and give the header its space back, or it keeps a gap above itself
        // for a bar that is no longer on the page.
        stopAuto('dismissed');
        publishHeight();
        // A timestamp, not a flag — see the note above. The flag could never expire.
        try { window.localStorage.setItem(storageKey, String(Date.now())); } catch (e) { /* nothing to do */ }
      });
    }

    // ── arrows ────────────────────────────────────────────────────────────────────────
    function syncArrows() {
      if (!rail || !prev || !next) return;
      var overflowing = rail.scrollWidth > rail.clientWidth + 4;
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      prev.hidden = !overflowing || atStart;
      next.hidden = !overflowing || atEnd;
    }
    if (rail) {
      rail.addEventListener('scroll', syncArrows, { passive: true });
      window.addEventListener('resize', syncArrows);
      // Images settle after layout and change scrollWidth, so re-measure once they have.
      window.addEventListener('load', syncArrows);
      syncArrows();
    }
    function nudge(dir) {
      if (!rail) return;
      rail.scrollBy({ left: dir * Math.max(rail.clientWidth * 0.8, 160), behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { nudge(-1); stopAuto('interaction'); });
    if (next) next.addEventListener('click', function () { nudge(1); stopAuto('interaction'); });

    /* ── sticky, and telling the header how far to move down ────────────────────────────
       Measured rather than assumed: the strip's height changes with the lead text wrapping
       and with the viewport, and it must fall to 0 the moment the strip is dismissed or is
       not sticky at all — otherwise the header keeps a gap above it for a bar that is no
       longer there. */
    var isSticky = section.dataset.sticky === 'true';
    // Shopify's own section wrapper is the element that has to stick — the strip inside it is
    // boxed to 67px and could never stick within that. The wrapper takes no attributes from
    // Liquid, so it is classed here instead.
    var wrapper = section.parentElement;
    if (isSticky && wrapper) wrapper.classList.add('SaleBanner__Sticky');

    function publishHeight() {
      var h = (isSticky && !section.hidden) ? Math.round(section.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--sb-height', h + 'px');

      /*
        Offset the site header only if it is ACTUALLY sticky.

        Applying this in CSS put a 67px band of blank page under the announcement bar: this
        theme's header is `position: relative` unless the merchant switches its sticky header
        on, and `top` on a relative element shifts it rather than pinning it. Reading the
        computed position is the only way to tell, so it is done here — and re-read each time,
        because the merchant can toggle that setting in the editor without reloading.
      */
      var header = document.querySelector('.shopify-section--header');
      if (!header) return;
      var headerSticky = getComputedStyle(header).position === 'sticky';
      header.style.top = headerSticky ? h + 'px' : '';

      /*
        Tell the theme when the pinned stack above the page changes height.

        The theme's OverflowScroller (product info column, product thumbnails, collection
        filter sidebar) reads its sticky offset once, when theme.js boots — and theme.js runs
        before this file, while the strip is still `hidden`. Without this it keeps an offset
        that ignores the strip and parks the product title 67px under the sticky header.
        Fired only on a change: resize and the ResizeObserver both call this constantly.
      */
      var stack = h + (headerSticky ? 's' : '');
      if (stack !== lastStack) {
        lastStack = stack;
        document.dispatchEvent(new CustomEvent('theme:sticky-offset-change'));
      }
    }
    var lastStack = null;
    publishHeight();
    window.addEventListener('resize', publishHeight);
    if (window.ResizeObserver) new ResizeObserver(publishHeight).observe(section);

    /* ── moving to the next piece on its own ────────────────────────────────────────────

       On a phone roughly one piece fits at a time, so everything past the first is behind a
       swipe most people will never make. Advancing on a timer is what makes the rest of the
       sale discoverable at all.

       It stops permanently the moment the shopper touches the strip. Auto-advance that keeps
       fighting a person who has taken hold of it is worse than none: the tile they are
       reaching for slides out from under the finger, and on a 2-second cadence that is a mis-
       tap rather than an annoyance. Taking over is treated as intent, not as a pause.

       It also never runs for someone who has asked their device to reduce motion, holds while
       a panel is open or the tab is in the background, and stops if the shopper has already
       dismissed the strip. WCAG 2.2.2 wants moving content to be stoppable; scrolling it
       stops it, and the dismiss control removes it outright. */
    var autoSeconds = parseInt(section.dataset.autoplay, 10);
    var autoTimer = null;
    var autoDead = false;

    function stopAuto(why) {
      autoDead = why === 'interaction' ? true : autoDead;
      if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
    }

    function step() {
      if (!rail) return;
      var tile = rail.querySelector('.SaleBanner__Item');
      if (!tile) return;
      var by = tile.getBoundingClientRect().width + 8; // one tile plus the rail's gap
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      rail.scrollTo({ left: atEnd ? 0 : rail.scrollLeft + by, behavior: 'smooth' });
    }

    function startAuto() {
      if (autoDead || autoTimer) return;
      if (!(autoSeconds > 0)) return;
      // Phones only. On a wider screen the whole strip is already visible and moving it would
      // be motion for its own sake.
      if (window.innerWidth >= 720) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (section.hidden) return;
      if (!rail || rail.scrollWidth <= rail.clientWidth + 4) return; // nothing to advance to
      autoTimer = setInterval(function () {
        if (document.hidden) return;              // background tab: hold, do not advance
        if (document.querySelector('.SalePeek')) return; // a panel is open; leave it alone
        step();
      }, autoSeconds * 1000);
    }

    if (rail) {
      ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
        rail.addEventListener(evt, function () { stopAuto('interaction'); }, { passive: true });
      });
    }
    window.addEventListener('resize', function () {
      // Crossing into desktop should stop it; crossing back should not resurrect it if the
      // shopper already took control.
      if (window.innerWidth >= 720) { if (autoTimer) { clearInterval(autoTimer); autoTimer = null; } }
      else startAuto();
    });
    startAuto();

    // ── the peek panel ────────────────────────────────────────────────────────────────
    var root = null;
    var lastFocus = null;
    var cache = {};

    function close() {
      if (!root) return;
      root.hidden = true;
      root.innerHTML = '';
      document.removeEventListener('keydown', onKey);
      // Back to the tile they opened, not the top of the document — dropping focus there
      // loses a keyboard user their place entirely.
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function onKey(e) { if (e.key === 'Escape') close(); }

    function ensureRoot() {
      if (root) return root;
      root = el('div', 'SalePeek__Root');
      root.hidden = true;
      document.body.appendChild(root);
      return root;
    }

    function position(panel, anchor) {
      // Phone: the CSS pins it to the bottom as a sheet and nothing needs measuring.
      if (window.innerWidth < 720) return;
      var r = anchor.getBoundingClientRect();
      var width = 380;
      var left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      panel.style.left = left + 'px';
      panel.style.right = 'auto';

      /*
        Flip above the strip when there is more room up there. The banner sits at the very top
        of the page, so below is almost always right — but on a short window, or if a merchant
        moves the section down the page, a panel that opens downward would be cut off by the
        viewport with its Add button unreachable.
      */
      var below = window.innerHeight - r.bottom;
      if (below < 320 && r.top > below) {
        panel.style.bottom = (window.innerHeight - r.top + 8) + 'px';
        panel.style.top = 'auto';
      } else {
        panel.style.top = (r.bottom + 8) + 'px';
        panel.style.bottom = 'auto';
      }
    }

    function render(product, anchor, url) {
      var host = ensureRoot();
      host.innerHTML = '';
      host.hidden = false;

      var scrim = el('button', 'SalePeek__Scrim');
      scrim.setAttribute('aria-label', 'Close');
      scrim.addEventListener('click', close);
      host.appendChild(scrim);

      var panel = el('div', 'SalePeek');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-label', product.title);

      var bar = el('div', 'SalePeek__Bar');
      bar.appendChild(el('span', 'SalePeek__Kicker', 'Quick look'));
      var x = el('button', 'SalePeek__Close', '✕');
      x.type = 'button';
      x.setAttribute('aria-label', 'Close');
      x.addEventListener('click', close);
      bar.appendChild(x);
      panel.appendChild(bar);

      var scroll = el('div', 'SalePeek__Scroll');

      if (product.featured_image) {
        var img = new Image();
        img.className = 'SalePeek__Shot';
        img.alt = '';
        img.src = product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_800x$1$2');
        scroll.appendChild(img);
      }

      var info = el('div', 'SalePeek__Info');
      if (product.vendor) info.appendChild(el('div', 'SalePeek__Vendor', product.vendor));
      info.appendChild(el('h2', 'SalePeek__Title', product.title));

      var priceWrap = el('span', 'SalePeek__Price');
      info.appendChild(priceWrap);

      /*
        One variant is selected at a time and the panel always reflects THAT variant — its
        price, its availability. Showing the product's cheapest price beside a chosen sold-out
        size is how a shopper ends up at a checkout that disagrees with the page they came
        from.
      */
      var current = null;
      var selection = {};

      function variantMatching() {
        for (var i = 0; i < product.variants.length; i++) {
          var v = product.variants[i];
          var ok = true;
          for (var j = 0; j < product.options.length; j++) {
            var name = product.options[j].name || product.options[j];
            if (selection[name] && v.options[j] !== selection[name]) { ok = false; break; }
          }
          if (ok) return v;
        }
        return null;
      }

      function paintPrice() {
        priceWrap.innerHTML = '';

        /*
          A price even before a size is chosen.

          On a two-option piece — most of this catalogue, since tops and bottoms are sized
          separately — nothing resolves to a variant until both are picked, and this returned
          early. So a shopper tapped a tile reading $81.00 and got a panel with a title, no
          figure at all, and a disabled button: the one number that made them tap disappeared
          at the moment they showed interest.

          The product's own lowest price is exactly what the tile showed, so showing that
          until a variant resolves keeps the two surfaces agreeing rather than blanking one.
          It is replaced by the real variant price the instant a size is chosen, and marked
          "From" when the sizes are not all one price, so it never states a figure the
          shopper cannot actually get.
        */
        var v = current;
        var provisional = false;
        if (!v) {
          v = { price: product.price, compare_at_price: product.compare_at_price };
          provisional = true;
        }
        if (v.price == null) return;

        var lead = (provisional && product.price_varies) ? 'From ' : '';
        var b = el('b', null, lead + formatMoney(v.price));
        priceWrap.appendChild(b);
        if (v.compare_at_price && v.compare_at_price > v.price) {
          var s = el('s');
          var lbl = el('span', 'visually-hidden', 'Was ');
          s.appendChild(lbl);
          s.appendChild(document.createTextNode(formatMoney(v.compare_at_price)));
          priceWrap.appendChild(s);
          var pct = Math.floor((v.compare_at_price - v.price) / v.compare_at_price * 100);
          priceWrap.appendChild(el('em', null, '−' + pct + '%'));
        }
      }

      var addBtn = el('button', 'SalePeek__Add');
      addBtn.type = 'button';
      var err = el('p', 'SalePeek__Error');
      err.hidden = true;

      function paintButton() {
        if (!current) {
          addBtn.disabled = true;
          addBtn.textContent = 'Choose an option';
        } else if (!current.available) {
          addBtn.disabled = true;
          addBtn.textContent = 'Sold out';
        } else {
          addBtn.disabled = false;
          addBtn.textContent = 'Add to bag — ' + formatMoney(current.price);
        }
      }

      // Options, only when there is genuinely a choice. A single-variant piece gets no picker.
      var hasChoice = product.variants.length > 1;
      if (hasChoice) {
        for (var oi = 0; oi < product.options.length; oi++) {
          (function (index) {
            var opt = product.options[index];
            var name = opt.name || opt;
            var values = [];
            product.variants.forEach(function (v) {
              if (values.indexOf(v.options[index]) === -1) values.push(v.options[index]);
            });

            var group = el('div', 'SalePeek__Opt');
            group.appendChild(el('div', 'SalePeek__OptName', name));
            var row = el('div', 'SalePeek__Values');

            values.forEach(function (val) {
              var btn = el('button', 'SalePeek__Value', val);
              btn.type = 'button';
              btn.setAttribute('aria-pressed', 'false');

              // Sold out at the value level, so a shopper is told before choosing rather
              // than after.
              var anyAvailable = product.variants.some(function (v) {
                return v.options[index] === val && v.available;
              });
              if (!anyAvailable) btn.disabled = true;

              btn.addEventListener('click', function () {
                selection[name] = val;
                row.querySelectorAll('.SalePeek__Value').forEach(function (b) {
                  b.setAttribute('aria-pressed', String(b === btn));
                });
                current = variantMatching();
                paintPrice();
                paintButton();
              });
              row.appendChild(btn);
            });

            group.appendChild(row);
            info.appendChild(group);
          })(oi);
        }
        /*
          Pre-select ONLY when there is a single thing to choose.

          With two groups — which is most of this catalogue, since tops and bottoms are sized
          separately — pre-selecting silently chose a bottom size the shopper had never seen,
          and left Add enabled reading a real price. Someone picks their top size, presses an
          enabled button, and receives a bottom in a size they never chose. On swimwear that is
          a wrong-size order and a return, not a cosmetic problem.

          So on multi-option products every group must be chosen deliberately; paintButton
          already says "Choose an option" until one resolves to a variant.
        */
        if (product.options.length === 1) {
          var firstAvailable = product.variants.filter(function (v) { return v.available; })[0];
          if (firstAvailable) {
            selection[product.options[0].name || product.options[0]] = firstAvailable.options[0];
            current = firstAvailable;
          }
        }
      } else {
        current = product.variants[0];
      }

      paintPrice();
      paintButton();

      // Reflect any pre-selection in the buttons, so what is highlighted matches the price.
      info.querySelectorAll('.SalePeek__Opt').forEach(function (group, gi) {
        var name = (product.options[gi] && (product.options[gi].name || product.options[gi]));
        group.querySelectorAll('.SalePeek__Value').forEach(function (b) {
          b.setAttribute('aria-pressed', String(selection[name] === b.textContent));
        });
      });

      addBtn.addEventListener('click', function () {
        if (!current) return;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';
        err.hidden = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: current.id, quantity: 1 }] })
        })
          .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error(res.body && res.body.description || 'Could not add to bag');
            addBtn.textContent = 'Added';
            /*
              Hand off to the theme's own cart, using the event the theme actually listens for.

              This first shipped dispatching 'cart:build' and 'cart:updated' — names this theme
              has never heard of. The item really was added, but the header count never moved
              and no drawer opened, so the shopper had no evidence anything had happened. An
              add you cannot see is barely better than one that failed.

              `product:added` on document is what theme.js binds (assets/theme.js:3554). Its
              handler increments the count, re-renders the cart and opens the cart drawer, so
              adding from here now behaves exactly like adding from a product page. Only this
              one event is sent: 'cart:refresh' would re-render a second time and the count
              would visibly jump twice.
            */
            document.dispatchEvent(new CustomEvent('product:added', {
              bubbles: true,
              detail: { quantity: 1, variant: current }
            }));
            // The drawer is about to take over the screen; the panel should not be behind it.
            setTimeout(close, 300);
          })
          .catch(function (e) {
            err.textContent = e.message || 'Could not add to bag';
            err.hidden = false;
            paintButton();
          });
      });

      /*
        The buy action lives in its own footer, OUTSIDE the scrolling region.

        It was `position: sticky` at the bottom of the scroller, which meant it painted on top
        of whatever happened to be in the last 58px — and on a two-option product that was
        always the first size picker. Hit-tested at 320x568, every one of the eight size
        buttons was unreachable: four returned the Add button from elementFromPoint and four
        were off-screen entirely. The panel looked complete and could not be used.

        A flex footer cannot overlap anything, is pinned at every panel height for free, and
        gives the error message somewhere to be seen — appended after a sticky button it fell
        outside the panel's clipped box, so a rejected add showed the shopper nothing at all
        while the button quietly returned to its original label.
      */
      var more = el('a', 'SalePeek__More', 'See the full details');
      more.href = url;
      info.appendChild(more);

      var foot = el('div', 'SalePeek__Foot');
      foot.appendChild(err);
      foot.appendChild(addBtn);

      scroll.appendChild(info);
      panel.appendChild(scroll);
      panel.appendChild(foot);
      host.appendChild(panel);

      position(panel, anchor);
      document.addEventListener('keydown', onKey);
      x.focus();
    }

    function open(anchor) {
      var handle = anchor.dataset.handle;
      var url = anchor.dataset.url;
      lastFocus = anchor;

      if (cache[handle]) { render(cache[handle], anchor, url); return; }

      fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('unavailable');
          return r.json();
        })
        .then(function (p) { cache[handle] = p; render(p, anchor, url); })
        .catch(function () {
          // If the panel cannot be built, the product page still works — send them there
          // rather than leaving a tile that does nothing when tapped.
          window.location.href = url;
        });
    }

    section.addEventListener('click', function (e) {
      var tile = e.target.closest ? e.target.closest('[data-peek]') : null;
      if (tile && section.contains(tile)) open(tile);
    });
  }

  function boot() {
    document.querySelectorAll('[data-section-type="sale-banner"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders a section on every settings change, and the new markup
  // arrives without its listeners.
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector
      ? e.target.querySelector('[data-section-type="sale-banner"]')
      : null;
    if (s) init(s);
    else if (e.target.matches && e.target.matches('[data-section-type="sale-banner"]')) init(e.target);
  });
})();
